import React, { useState } from 'react';
import { useDeepCompareEffect } from 'use-deep-compare';

import { Button, GradientProgressBar, IconWarningSign } from '@trqk/components';
import { useProgressCycle } from '@trqk/hooks';
import { locale } from '@trqk/utils';

import iconUrl from '../../../../../../assets/svg/insights-empty-gap-analysis.svg';
import { StepWrapper } from '../..';
import { getEmptyDataMessage } from './utils';
import {
    usePaymentPlanStatus,
    useGapAnalysisTools,
} from '../../../../../../hooks';

/**
 * Displays a progress bar and a message about the next steps in the process. The user cannot click
 * the "Continue" button until a successful log event for `detectionBatchAddedToGap` has been
 * detected in CreateGapAnalysis.
 * @param props - {@link GapAnalysisStepProps}
 */
export const AnalyzingData: GapAnalysisStepComponent = props => {
    const { context, remove, send } = props;
    const { fetchAndSetChannels, fetchAndSetTrqkGap } = useGapAnalysisTools();
    const [inProgressLabel, setInProgressLabel] = useState(
        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.ANALYZING_DATA.IN_PROGRESS_LABEL
            .ANALYZING_DATA,
    );
    const gapAnalysisId = context?.trqkGap?.gap_analysis_id ?? null;
    const { canUploadDetections } = usePaymentPlanStatus();

    const logDetails: LogDetail[] =
        gapAnalysisId === null ? [] : context.logDetails?.[gapAnalysisId] ?? [];
    const success = logDetails.find(
        (logDetail: LogDetail): boolean => logDetail.status === 'success',
    );
    const failure = logDetails.find(
        (logDetail: LogDetail): boolean => logDetail.status === 'failure',
    );
    const hasEmptyData =
        success?.detection_count === 0 || success?.channel_count === 0;

    const [showWarning, setShowWarning] = useState(false);
    const [showLoadingSpinner, setShowLoadingSpinner] = useState(true);

    const { progress } = useProgressCycle({
        delay: 100,
        stepSize: 1,
    });

    useDeepCompareEffect(() => {
        if (progress === 100 && gapAnalysisId) {
            // The progress cycle is used to give the illusion of a progress bar, but we don't
            // actually move forward unless we detect empty data, success, or error.

            // We must check for empty data first; it's a subset of the `success` condition.
            if (hasEmptyData) {
                setShowWarning(true);
                setShowLoadingSpinner(false);
                setInProgressLabel(
                    locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.ANALYZING_DATA
                        .IN_PROGRESS_LABEL.ANALYSIS_COMPLETE,
                );
            } else if (success) {
                send({
                    type: 'SET_STEP_HISTORY',
                    value: {
                        stepHistory: [
                            'selectPeriod',
                            'setUp',
                            'confirmChannels',
                        ],
                    },
                });

                send({
                    type: 'SET_LOADING',
                    value: {
                        loading: true,
                    },
                });

                Promise.all([
                    fetchAndSetTrqkGap(gapAnalysisId),
                    fetchAndSetChannels(gapAnalysisId),
                ])
                    .then(() => {
                        send({
                            type: 'SET_LOADING',
                            value: {
                                loading: false,
                            },
                        });
                    })
                    .catch(() => {
                        send({
                            type: 'SET_LOADING',
                            value: {
                                loading: false,
                            },
                        });
                    });
            } else if (failure) {
                setShowWarning(true);
                setShowLoadingSpinner(false);
                setInProgressLabel(
                    locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.ANALYZING_DATA
                        .IN_PROGRESS_LABEL.ANALYSIS_FAILED,
                );
            }
        }
    }, [progress, success, failure, hasEmptyData, gapAnalysisId]);

    const periodTitle = context.steps.selectPeriod.title;

    const label =
        periodTitle !== null
            ? `${locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.ANALYZING_DATA.PERFORMANCE_PERIOD} ${periodTitle}`
            : '';

    return (
        <StepWrapper context={context} remove={remove} send={send}>
            <div className="flex-grow flex flex-col items-center mt-12 space-y-6">
                <img src={iconUrl} alt="Gap Analysis icon" width={80} />
                <GradientProgressBar
                    progress={progress}
                    color="purple-to-aqua"
                    height={52}
                    inProgressLabel={inProgressLabel}
                    label={label}
                    loadingSpinner={showLoadingSpinner}
                />
                {!showWarning && (
                    <div>
                        <p className="my-4 font-semibold">
                            {
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                    .ANALYZING_DATA.AFTER_COMPLETE
                            }
                        </p>
                        <ol className="list-decimal leading-7">
                            <li>
                                {
                                    locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                        .ANALYZING_DATA.NEXT_STEPS.STEP_1
                                }
                            </li>
                            <li>
                                {
                                    locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                        .ANALYZING_DATA.NEXT_STEPS.STEP_2
                                }
                            </li>
                            <li>
                                {
                                    locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                        .ANALYZING_DATA.NEXT_STEPS.STEP_3
                                }
                            </li>
                        </ol>
                    </div>
                )}
                {showWarning && (
                    <div
                        className="mt-6 grid"
                        style={{
                            gridTemplateColumns: '30px auto 20px',
                            width: 500,
                        }}
                    >
                        <IconWarningSign fillColor="quinary-500" width={24} />
                        <div>
                            {failure && (
                                <p>
                                    {
                                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                            .ANALYZING_DATA.ERROR_MESSAGES
                                            .FAILURE
                                    }
                                </p>
                            )}
                            {hasEmptyData && (
                                <p>
                                    {getEmptyDataMessage(
                                        context.selectedPeriod,
                                    )}
                                </p>
                            )}
                            {canUploadDetections && (
                                <p className="mt-4">
                                    {
                                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                            .ANALYZING_DATA.ERROR_MESSAGES
                                            .HOW_TO_TRY_AGAIN
                                    }
                                </p>
                            )}
                            <Button
                                addClasses="mt-8"
                                autoWidth={true}
                                color="tertiary-light-bg"
                                handleClick={() => {
                                    send({
                                        type: 'UPDATE_STEP',
                                        value: {
                                            stepId: 'analyzingData',
                                            update: {
                                                disableNextStep: true,
                                            },
                                        },
                                    });

                                    if (gapAnalysisId) {
                                        // When re-trying a detection upload, we need to clear out
                                        // the log details array for this gapAnalysisId because the
                                        // same gapAnalysisId can be used for multiple detection
                                        // uploads. This prevents bugs where we might end up using
                                        // events that related to an earlier detection upload.
                                        send({
                                            type: 'CLEAR_LOG_DETAILS',
                                            value: {
                                                gapAnalysisId,
                                            },
                                        });
                                    }

                                    if (canUploadDetections) {
                                        send({
                                            type: 'SET_STEP_HISTORY',
                                            value: {
                                                stepHistory: [
                                                    'selectPeriod',
                                                    'setUp',
                                                    'uploadDetections',
                                                ],
                                            },
                                        });
                                    } else {
                                        send({
                                            type: 'SET_STEP_HISTORY',
                                            value: {
                                                stepHistory: [
                                                    'selectPeriod',
                                                    'setUp',
                                                ],
                                            },
                                        });
                                    }
                                }}
                            >
                                <p className="px-4">
                                    {
                                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                            .ANALYZING_DATA.ERROR_MESSAGES
                                            .TRY_AGAIN
                                    }
                                </p>
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </StepWrapper>
    );
};
